import React, { useEffect, useState } from 'react'
import { Col, Container, Row } from 'reactstrap'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { Drawer } from '@material-ui/core'
import { useSelector } from 'react-redux'
import { NumberFieldHookForm } from '../../../../layout/HookFormInputs'
import Button from '../../../../layout/Button'
import TooltipCustom from '../../../../common/Tooltip'
import { checkForDecimalAndNull, checkForNull, checkWhiteSpaces, number, percentageLimitValidation, blockInvalidNumberKeys, integerAndDecimalNumberLimit } from '../../../../../helper'
import { INCH_SQ_TO_METER_SQ } from '../../../../../config/constants'

const INITIAL_DATA_TO_SEND = {
    paintingSurfaceAreaInchSq: 0,
    paintingSurfaceAreaMeterSq: 0,
    sg: 0,
    nv: 0,
    dft: 0,
    transferEffPercent: 0,
    paintConspMlPart: 0,
    mixingRatioPercent: 0,
    consumptionInLtr: 0
}

function ConsumptionCalculator({ isOpen, onClose, onApply, onReset, onSurfaceAreaChange, initialData, partSurfaceArea, ViewMode = false }) {
    // Get initialConfiguration from Redux store
    const initialConfiguration = useSelector(state => state.auth.initialConfiguration)

    // State to store full precision values for API
    const [dataToSend, setDataToSend] = useState(INITIAL_DATA_TO_SEND)

    const {
        register,
        control,
        handleSubmit,
        setValue,
        getValues,
        reset,
        formState: { errors }
    } = useForm({
        mode: 'onChange',
        reValidateMode: 'onChange'
    })

    // Watch fields for calculations
    const surfaceAreaFields = useWatch({
        control,
        name: ['paintingSurfaceAreaInchSq']
    })

    const paintConspFields = useWatch({
        control,
        name: ['paintingSurfaceAreaMeterSq', 'sg', 'nv', 'dft', 'transferEffPercent']
    })

    const consumptionFields = useWatch({
        control,
        name: ['paintConspMlPart', 'mixingRatioPercent']
    })

    // Prefill data when drawer opens - use initialData if available, otherwise use partSurfaceArea
    useEffect(() => {
        if (isOpen) {
            if (initialData && Object.keys(initialData).length > 0) {
                // Prefill with existing data from API (using API key names)
                // Always use current partSurfaceArea (from table) - it takes priority
                const surfaceAreaValue = partSurfaceArea || initialData.PaintingSurfaceAreaInchSq || 0
                const surfaceAreaMeterSq = surfaceAreaValue * INCH_SQ_TO_METER_SQ

                reset({
                    paintingSurfaceAreaInchSq: surfaceAreaValue ? checkForDecimalAndNull(surfaceAreaValue, initialConfiguration?.NoOfDecimalForInputOutput) : '',
                    paintingSurfaceAreaMeterSq: surfaceAreaMeterSq ? checkForDecimalAndNull(surfaceAreaMeterSq, initialConfiguration?.NoOfDecimalForInputOutput) : '',
                    sg: initialData.SG ? checkForDecimalAndNull(initialData.SG, initialConfiguration?.NoOfDecimalForInputOutput) : '',
                    nv: initialData.NV ? checkForDecimalAndNull(initialData.NV, initialConfiguration?.NoOfDecimalForInputOutput) : '',
                    dft: initialData.DFT ? checkForDecimalAndNull(initialData.DFT, initialConfiguration?.NoOfDecimalForInputOutput) : '',
                    transferEffPercent: initialData.TransferEfficiencyPercentage ? checkForDecimalAndNull(initialData.TransferEfficiencyPercentage, initialConfiguration?.NoOfDecimalForInputOutput) : '',
                    paintConspMlPart: initialData.PaintConsumptionMlPerPart ? checkForDecimalAndNull(initialData.PaintConsumptionMlPerPart, initialConfiguration?.NoOfDecimalForInputOutput) : '',
                    mixingRatioPercent: initialData.MixingRatioPercentage ? checkForDecimalAndNull(initialData.MixingRatioPercentage, initialConfiguration?.NoOfDecimalForInputOutput) : '',
                    consumptionInLtr: initialData.ConsumptionInLiter ? checkForDecimalAndNull(initialData.ConsumptionInLiter, initialConfiguration?.NoOfDecimalForInputOutput) : ''
                })
                setDataToSend({
                    paintingSurfaceAreaInchSq: checkForNull(surfaceAreaValue),
                    paintingSurfaceAreaMeterSq: checkForNull(surfaceAreaMeterSq),
                    sg: checkForNull(initialData.SG),
                    nv: checkForNull(initialData.NV),
                    dft: checkForNull(initialData.DFT),
                    transferEffPercent: checkForNull(initialData.TransferEfficiencyPercentage),
                    paintConspMlPart: checkForNull(initialData.PaintConsumptionMlPerPart),
                    mixingRatioPercent: checkForNull(initialData.MixingRatioPercentage),
                    consumptionInLtr: checkForNull(initialData.ConsumptionInLiter)
                })
            } else {
                // Prefill with partSurfaceArea if available, otherwise empty
                const surfaceAreaValue = partSurfaceArea || 0
                const surfaceAreaMeterSq = surfaceAreaValue * INCH_SQ_TO_METER_SQ

                reset({
                    paintingSurfaceAreaInchSq: surfaceAreaValue ? checkForDecimalAndNull(surfaceAreaValue, initialConfiguration?.NoOfDecimalForInputOutput) : '',
                    paintingSurfaceAreaMeterSq: surfaceAreaValue ? checkForDecimalAndNull(surfaceAreaMeterSq, initialConfiguration?.NoOfDecimalForInputOutput) : '',
                    sg: '',
                    nv: '',
                    dft: '',
                    transferEffPercent: '',
                    paintConspMlPart: '',
                    mixingRatioPercent: '',
                    consumptionInLtr: ''
                })
                setDataToSend({
                    ...INITIAL_DATA_TO_SEND,
                    paintingSurfaceAreaInchSq: surfaceAreaValue,
                    paintingSurfaceAreaMeterSq: surfaceAreaMeterSq
                })
            }
        }
    }, [isOpen, initialData, partSurfaceArea, reset, initialConfiguration?.NoOfDecimalForInputOutput])

    // Calculate Painting Surface Area (Meter Sq)
    useEffect(() => {
        if (surfaceAreaFields) {
            const inchSq = checkForNull(getValues('paintingSurfaceAreaInchSq'))
            const meterSq = inchSq * INCH_SQ_TO_METER_SQ

            // Update UI with formatted value
            setValue('paintingSurfaceAreaMeterSq', checkForDecimalAndNull(meterSq, initialConfiguration?.NoOfDecimalForInputOutput))

            // Update dataToSend with full precision
            setDataToSend(prev => ({
                ...prev,
                paintingSurfaceAreaInchSq: inchSq,
                paintingSurfaceAreaMeterSq: meterSq
            }))
        }
    }, [surfaceAreaFields, setValue, getValues, initialConfiguration?.NoOfDecimalForInputOutput])

    // Calculate Paint Consp ml/part
    useEffect(() => {
        if (paintConspFields) {
            const meterSq = checkForNull(dataToSend.paintingSurfaceAreaMeterSq)
            const sg = checkForNull(getValues('sg'))
            const nv = checkForNull(getValues('nv'))
            const dft = checkForNull(getValues('dft'))
            const transferEffPercent = checkForNull(getValues('transferEffPercent'))

            let paintConspMlPart = 0
            if (sg > 0 && nv > 0 && transferEffPercent > 0) {
                paintConspMlPart = (meterSq * dft) / (nv * (transferEffPercent / 100)) / sg
            }

            // Update UI with formatted value
            setValue('paintConspMlPart', checkForDecimalAndNull(paintConspMlPart, initialConfiguration?.NoOfDecimalForInputOutput))

            // Update dataToSend with full precision
            setDataToSend(prev => ({
                ...prev,
                sg: sg,
                nv: nv,
                dft: dft,
                transferEffPercent: transferEffPercent,
                paintConspMlPart: paintConspMlPart
            }))
        }
    }, [paintConspFields, setValue, getValues, initialConfiguration?.NoOfDecimalForInputOutput, dataToSend.paintingSurfaceAreaMeterSq])

    // Calculate Consumption in Ltr
    useEffect(() => {
        if (consumptionFields) {
            const paintConspMlPart = checkForNull(dataToSend.paintConspMlPart)
            const mixingRatioPercent = checkForNull(getValues('mixingRatioPercent'))

            let consumptionInLtr = 0
            if (mixingRatioPercent > 0) {
                consumptionInLtr = paintConspMlPart * (mixingRatioPercent / 100) / 1000
            }

            // Prevent scientific notation (e-7) - if output is scientific notation, set to 0
            if (String(consumptionInLtr).includes('e')) {
                consumptionInLtr = 0
            }

            // Update UI with formatted value
            setValue('consumptionInLtr', checkForDecimalAndNull(consumptionInLtr, initialConfiguration?.NoOfDecimalForInputOutput))

            // Update dataToSend with full precision
            setDataToSend(prev => ({
                ...prev,
                mixingRatioPercent: mixingRatioPercent,
                consumptionInLtr: consumptionInLtr
            }))
        }
    }, [consumptionFields, setValue, getValues, initialConfiguration?.NoOfDecimalForInputOutput, dataToSend.paintConspMlPart])

    // Handle apply/save button click - send all fields with API key names
    const handleApply = () => {
        const apiData = {
            PaintingSurfaceAreaInchSq: dataToSend.paintingSurfaceAreaInchSq,
            PaintingSurfaceAreaMeterSq: dataToSend.paintingSurfaceAreaMeterSq,
            SG: dataToSend.sg,
            NV: dataToSend.nv,
            DFT: dataToSend.dft,
            TransferEfficiencyPercentage: dataToSend.transferEffPercent,
            MixingRatioPercentage: dataToSend.mixingRatioPercent,
            PaintConsumptionMlPerPart: dataToSend.paintConspMlPart,
            ConsumptionInLiter: dataToSend.consumptionInLtr
        }
        onApply(apiData)
    }

    // Handle reset button click - clear calculator data
    const handleReset = () => {
        // Reset form to initial state with only partSurfaceArea
        const surfaceAreaValue = partSurfaceArea || 0
        const surfaceAreaMeterSq = surfaceAreaValue * INCH_SQ_TO_METER_SQ

        reset({
            paintingSurfaceAreaInchSq: surfaceAreaValue ? checkForDecimalAndNull(surfaceAreaValue, initialConfiguration?.NoOfDecimalForInputOutput) : '',
            paintingSurfaceAreaMeterSq: surfaceAreaValue ? checkForDecimalAndNull(surfaceAreaMeterSq, initialConfiguration?.NoOfDecimalForInputOutput) : '',
            sg: '',
            nv: '',
            dft: '',
            transferEffPercent: '',
            paintConspMlPart: '',
            mixingRatioPercent: '',
            consumptionInLtr: ''
        })
        setDataToSend({
            ...INITIAL_DATA_TO_SEND,
            paintingSurfaceAreaInchSq: surfaceAreaValue,
            paintingSurfaceAreaMeterSq: surfaceAreaMeterSq
        })

        // Notify parent to clear calculator data
        if (onReset) {
            onReset()
        }
    }

    return (
        <Drawer anchor="right" open={isOpen}>
            <Container>
                <div className="drawer-wrapper layout-width-800px">
                    <Row className="drawer-heading">
                        <Col>
                            <div className="header-wrapper left">
                                <h3>Consumption Calculator</h3>
                            </div>
                            <div onClick={onClose} className="close-button right" />
                        </Col>
                    </Row>
                    <form onSubmit={handleSubmit(handleApply)}>
                        <Row>
                            <Col md="4">
                                <NumberFieldHookForm
                                    label="Painting Surface Area (Inch Sq)"
                                    name="paintingSurfaceAreaInchSq"
                                    Controller={Controller}
                                    control={control}
                                    register={register}
                                    rules={{
                                        validate: { number, checkWhiteSpaces, integerAndDecimalNumberLimit }
                                    }}
                                    onKeyDown={blockInvalidNumberKeys}
                                    handleChange={() => {}}
                                    defaultValue=""
                                    customClassName="withBorder"
                                    errors={errors.paintingSurfaceAreaInchSq}
                                    disabled={ViewMode}
                                    hoverTitle={dataToSend?.paintingSurfaceAreaInchSq}
                                />
                            </Col>
                            <Col md="4">
                                <TooltipCustom
                                    disabledIcon={true}
                                    id="paintingSurfaceAreaMeterSq"
                                    tooltipText={`Painting Surface Area (Meter Sq) = Painting Surface Area (Inch Sq) × ${INCH_SQ_TO_METER_SQ}`}
                                />
                                <NumberFieldHookForm
                                    label="Painting Surface Area (Meter Sq)"
                                    name="paintingSurfaceAreaMeterSq"
                                    Controller={Controller}
                                    control={control}
                                    register={register}
                                    handleChange={() => {}}
                                    defaultValue=""
                                    customClassName="withBorder"
                                    disabled
                                    errors={errors.paintingSurfaceAreaMeterSq}
                                    hoverTitle={dataToSend?.paintingSurfaceAreaMeterSq}
                                />
                            </Col>
                            <Col md="4">
                                <NumberFieldHookForm
                                    label="S.G."
                                    name="sg"
                                    Controller={Controller}
                                    control={control}
                                    register={register}
                                    rules={{
                                        validate: { number, checkWhiteSpaces, integerAndDecimalNumberLimit }
                                    }}
                                    onKeyDown={blockInvalidNumberKeys}
                                    handleChange={() => {}}
                                    defaultValue=""
                                    customClassName="withBorder"
                                    errors={errors.sg}
                                    disabled={ViewMode}
                                    hoverTitle={dataToSend?.sg}
                                />
                            </Col>
                            <Col md="4">
                                <NumberFieldHookForm
                                    label="N.V."
                                    name="nv"
                                    Controller={Controller}
                                    control={control}
                                    register={register}
                                    rules={{
                                        validate: { number, checkWhiteSpaces, integerAndDecimalNumberLimit }
                                    }}
                                    onKeyDown={blockInvalidNumberKeys}
                                    handleChange={() => {}}
                                    defaultValue=""
                                    customClassName="withBorder"
                                    errors={errors.nv}
                                    disabled={ViewMode}
                                    hoverTitle={dataToSend?.nv}
                                />
                            </Col>
                            <Col md="4">
                                <NumberFieldHookForm
                                    label="DFT"
                                    name="dft"
                                    Controller={Controller}
                                    control={control}
                                    register={register}
                                    rules={{
                                        validate: { number, checkWhiteSpaces, integerAndDecimalNumberLimit }
                                    }}
                                    onKeyDown={blockInvalidNumberKeys}
                                    handleChange={() => {}}
                                    defaultValue=""
                                    customClassName="withBorder"
                                    errors={errors.dft}
                                    disabled={ViewMode}
                                    hoverTitle={dataToSend?.dft}
                                />
                            </Col>
                            <Col md="4">
                                <NumberFieldHookForm
                                    label="Transfer Eff. %"
                                    name="transferEffPercent"
                                    Controller={Controller}
                                    control={control}
                                    register={register}
                                    rules={{
                                        validate: { number, checkWhiteSpaces, percentageLimitValidation },
                                        max: {
                                            value: 100,
                                            message: 'Percentage cannot be greater than 100'
                                        }
                                    }}
                                    onKeyDown={blockInvalidNumberKeys}
                                    handleChange={() => {}}
                                    defaultValue=""
                                    customClassName="withBorder"
                                    errors={errors.transferEffPercent}
                                    disabled={ViewMode}
                                    hoverTitle={dataToSend?.transferEffPercent}
                                />
                            </Col>
                            <Col md="4">
                                <TooltipCustom
                                    disabledIcon={true}
                                    id="paintConspMlPart"
                                    tooltipText="Paint Consp (ml/part) = (Surface Area (m²) × DFT) / N.V./ S.G./ (Transfer Eff. % / 100))"
                                />
                                <NumberFieldHookForm
                                    label="Paint Consp ml/part"
                                    name="paintConspMlPart"
                                    Controller={Controller}
                                    control={control}
                                    register={register}
                                    handleChange={() => {}}
                                    defaultValue=""
                                    customClassName="withBorder"
                                    disabled
                                    errors={errors.paintConspMlPart}
                                    hoverTitle={dataToSend?.paintConspMlPart}
                                />
                            </Col>
                            <Col md="4">
                                <NumberFieldHookForm
                                    label="Mixing Ratio %"
                                    name="mixingRatioPercent"
                                    Controller={Controller}
                                    control={control}
                                    register={register}
                                    rules={{
                                        validate: { number, checkWhiteSpaces, percentageLimitValidation },
                                        max: {
                                            value: 100,
                                            message: 'Percentage cannot be greater than 100'
                                        }
                                    }}
                                    onKeyDown={blockInvalidNumberKeys}
                                    handleChange={() => {}}
                                    defaultValue=""
                                    customClassName="withBorder"
                                    errors={errors.mixingRatioPercent}
                                    disabled={ViewMode}
                                    hoverTitle={dataToSend?.mixingRatioPercent}
                                />
                            </Col>
                            <Col md="4">
                                <TooltipCustom
                                    disabledIcon={true}
                                    id="consumptionInLtr"
                                    tooltipText="Consumption in Ltr = (Paint Consp ml/part × (Mixing Ratio % / 100)) / 1000"
                                />
                                <NumberFieldHookForm
                                    label="Consumption in Ltr."
                                    name="consumptionInLtr"
                                    Controller={Controller}
                                    control={control}
                                    register={register}
                                    handleChange={() => {}}
                                    defaultValue=""
                                    customClassName="withBorder"
                                    disabled
                                    errors={errors.consumptionInLtr}
                                    hoverTitle={dataToSend?.consumptionInLtr}
                                />
                            </Col>
                        </Row>
                        <Row className="sticky-footer">
                            <Col md="12" className="text-right d-flex align-items-center justify-content-end">
                                <Button
                                    id="ConsumptionCalc_cancel"
                                    className="mr-2"
                                    variant="cancel-btn"
                                    onClick={onClose}
                                    icon="cancel-icon"
                                    buttonName={ViewMode ? "Close" : "Cancel"}
                                />
                                {!ViewMode && (
                                    <>
                                        <Button
                                            id="ConsumptionCalc_reset"
                                            className="mr-2"
                                            variant="reset-btn"
                                            onClick={handleReset}
                                            buttonName="Reset"
                                        />
                                        <Button
                                            id="ConsumptionCalc_save"
                                            type="submit"
                                            icon="save-icon"
                                            buttonName="Save"
                                        />
                                    </>
                                )}
                            </Col>
                        </Row>
                    </form>
                </div>
            </Container>
        </Drawer>
    )
}

export default ConsumptionCalculator
